import React, { useState } from "react";
import { signInWithEmailAndPassword, sendPasswordResetEmail, onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();

  // Redirect if already logged in
  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const { role } = userSnap.data();
          const redirectMap = {
            "salesman": "/base",
            "ds": "/ds-mobile",
            "warehouse": "/warehouse-close",
            "admin": "/operator",
            "superadmin": "/operator",
            "wholesale": "/wholesale-order",
            "swd": "/swd",
            "cashier": "/cashier-Approval",
            "collection": "/cashier",
            "distributor": "/distributor-mobile",
            "rsm": "/rsm-dashboard",
            "ds_manager": "/ds-manager"
          };
          const path = redirectMap[role] || "/pending-orders";
          navigate(path, { replace: true });
        }
      }
    });
    return () => unsub();
  }, [navigate]);

  const doLogin = async () => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const userRef = doc(db, "users", cred.user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        alert("System Access Denied: User role not mapped. Contact architecture team.");
        return;
      }

      const { role } = userSnap.data();

      // Standardized Redirection Matrix
      const redirectMap = {
        "salesman": "/base",
        "ds": "/ds-mobile",
        "warehouse": "/warehouse-close",
        "admin": "/operator",
        "superadmin": "/operator",
        "wholesale": "/wholesale-order",
        "swd": "/swd",
        "cashier": "/cashier-Approval",
        "collection": "/cashier",
        "distributor": "/distributor-mobile",
        "rsm": "/rsm-dashboard",
        "ds_manager": "/ds-manager"
      };

      const path = redirectMap[role] || "/pending-orders";
      navigate(path, { replace: true });

    } catch (err) {
      console.error(err);
      alert("Verification Failed: Secure handshake could not be established.");
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      alert("Please enter your identifier (email) to reset password.");
      return;
    }
    const ok = window.confirm(`Send password reset link to ${email}?`);
    if (!ok) return;

    try {
      await sendPasswordResetEmail(auth, email);
      alert("Reset link dispatched. Please check your inbox / spam folder.");
    } catch (err) {
      console.error(err);
      alert("Reset Failed: Could not dispatch link. Verify internal identifier.");
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.backgroundGlow}></div>
      <div style={styles.glassCard}>
        <div style={{ padding: "0 0 40px" }}>
          <div style={styles.logoContainer}>
            <img src="/logo.svg" alt="QuickBill" style={styles.logo} />
          </div>
          <h1 style={styles.title}>QuickBill <span style={{ color: "var(--primary)", fontWeight: 400 }}>Core</span></h1>
          <p style={styles.subtitle}>Autonomous Billing & Delivery Platform</p>
        </div>

        <div style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>USER IDENTIFIER</label>
            <input
              type="email"
              style={styles.input}
              placeholder="operator@system.node"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div style={styles.field}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <label style={styles.label}>PASSWORD</label>
              <span 
                onClick={handleResetPassword}
                style={{ fontSize: 10, fontWeight: 800, color: "var(--primary)", cursor: "pointer", letterSpacing: "0.05em" }}
              >
                FORGOT?
              </span>
            </div>
            <input
              type="password"
              style={styles.input}
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button 
            className="btn-primary" 
            style={styles.submitBtn} 
            onClick={doLogin}
          >
            LOGIN
          </button>
        </div>

        <div style={styles.footer}>
          System Status: <span style={{ color: "var(--success)" }}>OPERATIONAL</span> • V2.1.0-ENTERPRISE
        </div>
      </div>
      
      <div style={styles.floatingFooter}>
        POWERED BY <b>QUICKBILL ARCHITECTURE TEAM</b> • SECURE ISO/IEC 27001 ENVIRONMENT
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: "100vw", height: "100vh",
    background: "#050505",
    display: "flex", justifyContent: "center", alignItems: "center",
    position: "relative", overflow: "hidden",
    fontFamily: "'Inter', sans-serif"
  },
  backgroundGlow: {
    position: "absolute", top: "-50%", left: "-50%", width: "200%", height: "200%",
    background: "radial-gradient(circle at center, rgba(255, 107, 0, 0.15) 0%, transparent 40%), radial-gradient(circle at 80% 20%, rgba(236, 72, 153, 0.1) 0%, transparent 30%)",
    animation: "rotate 20s linear infinite"
  },
  glassCard: {
    position: "relative",
    width: "100%", maxWidth: 460,
    background: "rgba(15, 15, 15, 0.7)",
    backdropFilter: "blur(40px) saturate(180%)",
    padding: "64px 48px",
    borderRadius: 32,
    border: "1px solid rgba(255, 255, 255, 0.08)",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.05) inset",
    textAlign: "center",
    zIndex: 10
  },
  logoContainer: {
    width: 96, height: 96, margin: "0 auto 24px",
    background: "transparent",
    borderRadius: 24, padding: 20,
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 10px 20px -5px rgba(0,0,0,0.5)"
  },
  logo: { width: "100%", height: "100%", filter: "drop-shadow(0 0 10px rgba(255, 107, 0, 0.5))" },
  title: { fontSize: 36, fontWeight: 900, color: "#fff", marginBottom: 8, letterSpacing: "-0.04em" },
  subtitle: { color: "rgba(255,255,255,0.5)", fontSize: 13, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" },
  form: { display: "flex", flexDirection: "column", gap: 24, textAlign: "left" },
  field: { display: "flex", flexDirection: "column", gap: 8 },
  label: { fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.4)", letterSpacing: "0.05em" },
  input: {
    width: "100%", padding: "16px 20px", borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.03)",
    color: "#fff", fontSize: 16, outline: "none",
    transition: "all 0.2s ease", boxSizing: "border-box"
  },
  submitBtn: { width: "100%", padding: "20px", fontSize: 13, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 12, borderRadius: 16, border: "none", cursor: "pointer" },
  footer: { marginTop: 40, fontSize: 10, color: "rgba(255,255,255,0.3)", fontWeight: 700, letterSpacing: "0.1em" },
  floatingFooter: { position: "absolute", bottom: 40, color: "rgba(255,255,255,0.2)", fontSize: 10, letterSpacing: "0.1em", textAlign: "center", width: "100%", fontWeight: 500 }
};
